import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ARTICLES, getArticle } from "@/mocks/articles";
import { getCategory } from "@/mocks/categories";
import Breadcrumb from "@/components/ui/Breadcrumb";
import { formatViews, useJsonLd, usePageMeta } from "@/lib/site";

export default function ArticlePage() {
  const { slug = "" } = useParams();
  const article = getArticle(slug);
  const cat = article ? getCategory(article.category) : undefined;

  usePageMeta(
    article ? `${article.title} - 明星八卦圈内` : "内容不存在 - 明星八卦圈内",
    article ? article.excerpt : "你访问的内容不存在或已被下线。"
  );

  const ld =
    article && cat
      ? {
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Article",
              headline: article.title,
              description: article.excerpt,
              articleSection: cat.name,
              inLanguage: "zh-CN",
              datePublished: `${article.date}T09:00:00+08:00`,
              url: `https://yslcare.cn/article/${article.slug}`,
              image: article.image,
              author: {
                "@type": "Organization",
                name: "明星八卦圈内编辑部",
              },
              publisher: { "@type": "Organization", name: "明星八卦圈内" },
              mainEntityOfPage: `https://yslcare.cn/article/${article.slug}`,
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "首页",
                  item: "https://yslcare.cn/",
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: cat.name,
                  item: `https://yslcare.cn/category/${cat.key}`,
                },
                {
                  "@type": "ListItem",
                  position: 3,
                  name: article.title,
                  item: `https://yslcare.cn/article/${article.slug}`,
                },
              ],
            },
          ],
        }
      : null;
  useJsonLd(ld);

  if (!article || !cat) {
    return (
      <div className="container-page py-24 text-center">
        <p className="font-heading text-4xl font-black text-primary-500">404</p>
        <p className="mt-3 text-foreground-600">这篇文章不存在或已下线。</p>
        <Link
          to="/"
          className="c-btn mt-6 bg-primary-500 text-background-50 hover:bg-primary-600"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const related = [
    ...ARTICLES.filter((a) => a.category === article.category && a.slug !== slug),
    ...ARTICLES.filter((a) => a.category !== article.category),
  ].slice(0, 3);

  return (
    <ArticleBody
      article={article}
      catName={cat.name}
      catKey={cat.key}
      catIcon={cat.icon}
      related={related}
    />
  );
}

function ArticleBody({
  article,
  catName,
  catKey,
  catIcon,
  related,
}: {
  article: NonNullable<ReturnType<typeof getArticle>>;
  catName: string;
  catKey: string;
  catIcon: string;
  related: (NonNullable<ReturnType<typeof getArticle>>)[];
}) {
  const [liked, setLiked] = useState(false);
  const [saved, setSaved] = useState(false);
  const likeCount = article.likes + (liked ? 1 : 0);

  return (
    <section className="container-page pt-6 md:pt-8">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: catName, to: `/category/${catKey}` },
          { label: "正文" },
        ]}
      />
      <div className="mx-auto mt-6 max-w-3xl">
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to={`/category/${catKey}`}
            className="inline-flex items-center gap-1.5 rounded-sm bg-primary-100 px-2 py-1 text-xs font-semibold text-primary-700 transition-colors hover:bg-primary-200"
          >
            <i className={catIcon}></i>
            {catName}
          </Link>
          <span className="inline-flex items-center gap-1.5 text-xs text-foreground-500">
            <i className="ri-eye-off-line"></i>
            匿名信源 · {article.sourceRole}
          </span>
          {article.hot && (
            <span className="inline-flex items-center gap-1 rounded-sm bg-accent-100 px-2 py-1 text-xs font-semibold text-accent-700">
              <i className="ri-fire-line"></i>本周热瓜
            </span>
          )}
        </div>
        <h1 className="mt-4 font-heading text-3xl font-black leading-snug text-foreground-950 md:text-4xl md:leading-snug">
          {article.title}
        </h1>
        <p className="mt-4 text-base leading-relaxed text-foreground-600">
          {article.excerpt}
        </p>
        <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 border-y border-background-200 py-3 text-sm text-foreground-500">
          <span className="inline-flex items-center gap-1.5">
            <i className="ri-calendar-line text-primary-500"></i>
            {article.date}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <i className="ri-time-line"></i>
            阅读约 {article.readTime}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <i className="ri-eye-line"></i>
            {formatViews(article.views)} 人读过
          </span>
          <span className="inline-flex items-center gap-1.5">
            <i className="ri-heart-3-line text-primary-500"></i>
            {formatViews(article.likes)} 热评
          </span>
        </div>

        <div className="mt-8 flex flex-col gap-8 md:flex-row">
          <article className="min-w-0 flex-1">
            <figure className="overflow-hidden rounded-lg border border-background-200">
              <img
                src={article.image}
                alt={`${article.title} 配图`}
                className="h-full w-full object-cover"
              />
            </figure>
            <div className="mt-6 space-y-6">
              {article.blocks.map((b, i) => {
                if (b.type === "h") {
                  return (
                    <h2
                      key={i}
                      className="flex items-center gap-2.5 pt-2 font-heading text-xl font-extrabold text-foreground-950"
                    >
                      <span className="h-6 w-1 rounded-full bg-accent-500"></span>
                      {b.text}
                    </h2>
                  );
                }
                if (b.type === "quote") {
                  return (
                    <blockquote
                      key={i}
                      className="rounded-lg border-l-4 border-accent-500 bg-accent-100 px-5 py-4"
                    >
                      <p className="font-heading text-base font-semibold leading-relaxed text-foreground-900">
                        {b.text}
                      </p>
                      {b.cite && (
                        <cite className="mt-2 block text-xs not-italic text-foreground-500">
                          —— {b.cite}
                        </cite>
                      )}
                    </blockquote>
                  );
                }
                if (b.type === "tip") {
                  return (
                    <div
                      key={i}
                      className="flex items-start gap-3 rounded-lg border border-primary-200 bg-primary-50 px-5 py-4"
                    >
                      <i className="ri-lightbulb-line mt-0.5 text-lg text-primary-500"></i>
                      <p className="text-sm leading-relaxed text-foreground-800">
                        {b.text}
                      </p>
                    </div>
                  );
                }
                return (
                  <p key={i} className="text-[15px] leading-8 text-foreground-800">
                    {b.text}
                  </p>
                );
              })}
            </div>

            {/* 互动 */}
            <div className="mt-8 flex flex-wrap items-center gap-3 border-t border-background-200 pt-6">
              <button
                type="button"
                onClick={() => setLiked((v) => !v)}
                className={`inline-flex items-center gap-2 rounded-full border px-5 py-2.5 text-sm font-semibold transition-colors ${
                  liked
                    ? "border-primary-500 bg-primary-500 text-background-50"
                    : "border-background-300 text-foreground-700 hover:border-primary-400 hover:text-primary-600"
                }`}
              >
                <i className={`${liked ? "ri-heart-fill" : "ri-heart-3-line"}`}></i>
                觉得这瓜带劲 {formatViews(likeCount)}
              </button>
              <button
                type="button"
                onClick={() => setSaved((v) => !v)}
                className={`inline-flex items-center gap-2 rounded-full border px-5 py-2.5 text-sm font-semibold transition-colors ${
                  saved
                    ? "border-accent-500 bg-accent-500 text-background-50"
                    : "border-background-300 text-foreground-700 hover:border-accent-500 hover:text-accent-600"
                }`}
              >
                <i className={`${saved ? "ri-bookmark-fill" : "ri-bookmark-line"}`}></i>
                {saved ? "已收藏" : "收藏吃瓜"}
              </button>
              <div className="ml-auto flex flex-wrap gap-2">
                {article.tags.map((t) => (
                  <span
                    key={t}
                    className="inline-flex items-center gap-1 rounded-sm bg-secondary-100 px-2 py-1 text-xs text-secondary-900"
                  >
                    <i className="ri-price-tag-3-line"></i>
                    {t}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-6 flex items-start gap-3 rounded-lg bg-secondary-100 p-4 text-sm text-foreground-600">
              <i className="ri-shield-check-line mt-0.5 text-secondary-500"></i>
              <p>
                免责声明：本文为虚构的行业观察与匿名信源叙述，其中人物均无特定指向，仅供娱乐参考。
                平台坚持「来源可信、拒绝造谣」，请勿对号入座，也欢迎通过投稿通道与我们核实与沟通。
              </p>
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to={`/category/${catKey}`}
                className="c-btn bg-primary-100 text-primary-700 hover:bg-primary-200"
              >
                <i className="ri-arrow-left-line mr-1.5"></i>
                返回{catName}
              </Link>
              <Link
                to="/about#contact"
                className="c-btn border border-background-300 text-foreground-700 hover:border-primary-400 hover:text-primary-600"
              >
                <i className="ri-quill-pen-line mr-1.5"></i>
                我要投稿
              </Link>
            </div>
          </article>

          {/* 侧栏推荐 */}
          <aside className="w-full shrink-0 md:w-72">
            <div className="sticky top-20 rounded-lg border border-background-200 bg-background-100 p-4">
              <h3 className="flex items-center gap-2 font-heading text-base font-extrabold text-foreground-950">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                  <i className="ri-file-list-3-line"></i>
                </span>
                猜你喜欢
              </h3>
              <ul className="mt-3 divide-y divide-background-200">
                {related.map(
                  (r) =>
                    r && (
                      <li key={r.slug} className="py-3">
                        <Link to={`/article/${r.slug}`} className="group flex gap-3">
                          <div className="relative h-14 w-20 shrink-0 overflow-hidden rounded-md">
                            <img
                              src={r.image}
                              alt={r.title}
                              className="h-full w-full object-cover"
                            />
                          </div>
                          <p className="line-clamp-3 text-sm leading-snug text-foreground-800 transition-colors group-hover:text-primary-600">
                            {r.title}
                          </p>
                        </Link>
                      </li>
                    )
                )}
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}